/**
 * DatePicker Component
 * Reusable date picker with optional time selection
 *
 * Features:
 * - "Box" layout with Header, Body, Footer
 * - View swapping between Calendar and Time Picker
 * - 24-hour time format (HH:mm)
 * - Optional time selection via includeTime prop
 */

import * as React from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { TimePicker } from '@/components/ui/time-picker';

export interface DatePickerProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  placeholder?: string;
  className?: string;
  includeTime?: boolean;
}

export function DatePicker({
  value,
  onChange,
  disabled = false,
  disableFuture = false,
  disablePast = false,
  placeholder = 'Select date',
  className,
  includeTime = false,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeView, setActiveView] = React.useState<'calendar' | 'time'>('calendar');

  // Parse ISO string to Date
  const parseDate = (isoStr: string | undefined): Date | undefined => {
    if (!isoStr) return undefined;
    const d = new Date(isoStr);
    return isNaN(d.getTime()) ? undefined : d;
  };

  const selectedDate = parseDate(value);

  // Format helpers
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Select Date';
    return format(date, 'yyyy-MM-dd');
  };

  const formatTime = (date: Date | undefined) => {
    if (!date) return '00:00';
    return format(date, 'HH:mm');
  };

  const displayValue = React.useMemo(() => {
    if (!selectedDate) return '';
    return includeTime
      ? format(selectedDate, 'yyyy-MM-dd HH:mm')
      : format(selectedDate, 'yyyy-MM-dd');
  }, [selectedDate, includeTime]);

  // Handlers
  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) {
      onChange(undefined);
      return;
    }

    const newDate = new Date(date);
    // Preserve time if exists
    if (selectedDate) {
      newDate.setHours(selectedDate.getHours(), selectedDate.getMinutes());
    } else {
      // Default to 00:00 if no previous time
      newDate.setHours(0, 0, 0, 0);
    }

    onChange(newDate.toISOString());
  };

  const handleTimeChange = (newDate: Date) => {
    onChange(newDate.toISOString());
  };

  const handleClear = () => {
    onChange(undefined);
    setActiveView('calendar');
  };

  const handleApply = () => {
    setIsOpen(false);
    setActiveView('calendar');
  };

  return (
    <div className={cn('flex gap-2', className)}>
      <Popover open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setActiveView('calendar');
      }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              // Flat like a field, not raised like a button: this trigger *is* the
              // date field, so it drops the outline variant's shadow-sm. The empty
              // state is a placeholder to the user even though it is a <button>, so
              // it takes the placeholder colour.
              "w-full justify-start text-left font-normal shadow-none",
              !value && "text-placeholder",
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <span>{displayValue || placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border p-3 bg-muted/10">
              <div className="flex items-center gap-2 text-sm">
                <div
                  className={cn("cursor-pointer px-2 py-1 rounded hover:bg-accent", activeView === 'calendar' && "font-semibold")}
                  onClick={() => setActiveView('calendar')}
                >
                  {formatDate(selectedDate)}
                </div>
                {includeTime && (
                  <div
                    className={cn("cursor-pointer px-2 py-1 rounded hover:bg-accent flex items-center gap-1", activeView === 'time' && "bg-primary text-primary-foreground")}
                    onClick={() => setActiveView('time')}
                  >
                    <Clock className="h-3 w-3" />
                    {formatTime(selectedDate)}
                  </div>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="p-0">
              {activeView === 'calendar' && (
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleCalendarSelect}
                  disabled={(date) => {
                    if (disableFuture && date > new Date()) return true;
                    if (disablePast && date < new Date()) return true;
                    return false;
                  }}
                  initialFocus
                  className="p-3"
                />
              )}
              {activeView === 'time' && (
                <div className="p-3 w-full flex justify-center">
                  <TimePicker
                    value={selectedDate}
                    onChange={handleTimeChange}
                    className="w-[280px]"
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-border p-3 flex justify-end gap-2 bg-muted/10">
              <Button variant="ghost" size="sm" onClick={handleClear}>Clear</Button>
              <Button size="sm" onClick={handleApply}>OK</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

